import { Component, Inject, LOCALE_ID, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { AppConstants } from "src/app/constants/constants";
import { Steps } from "src/app/constants/steps";
import { SetCourseId } from "src/app/_store/_store.actions";
import { LearnerCourseLog } from "src/app/_models/LearnerCourseLog";
import { filter, map, take, takeUntil } from "rxjs/operators";
import { Observable, Subject, combineLatest } from "rxjs";
import { GetOwnUserProfile } from "src/app/_store/user-profile/user-profile.actions";
import { LearnerCourse, UpdateLearnerCourseDto } from "src/app/_models/LearnerCourse";
import { selectLearnerCourseLoader, selectLearnerCourseLogError, selectLearnerCourseLogStatus, selectLearnerCourseLogStatusError, selectLearnerCourseLogStatusLoader, selectLearnerCourseResult, selectUpdateLearnerCourseLoader, selectUpdateLearnerCourseResult } from "src/app/_store/learner-course/learner-course.selectors";
import { GetLearnerCourse, GetLearnersLearnerCourseLogStatus, ResetLearnerCourseState, UpdateLearnerCourse } from "src/app/_store/learner-course/learner-course.actions";
import { HttpErrorResponse } from "@angular/common/http";
import { UserSessionService } from "src/app/_services/user-session.service";

@Component({
  selector: "app-learner-course",
  templateUrl: "./learner-course.component.html",
  styleUrls: ["./learner-course.component.scss"],
})
export class LearnerCourseComponent implements OnInit, OnDestroy {
  destroy$: Subject<void> = new Subject<void>();
  courseId: number = undefined;
  componentUrl: string = undefined;
  learnerId: number = undefined;
  lcLogId: number = undefined;
  siteLanguageCode: string;
  updateLearnerCourse: UpdateLearnerCourseDto;
  isNewDigLit: boolean = false;
  courseIconPath: string;
  statusResolved: boolean = false;

  combinedLoaders$: Observable<boolean>;
  public get STEPS(): typeof Steps {
    return Steps;
  }

  constructor(
    @Inject(LOCALE_ID) protected localeId: string,
    private router: Router,
    private route: ActivatedRoute,
    private store: Store,
    private userSessionService: UserSessionService
  ) {
    this.siteLanguageCode = localeId;
  }

  ngOnInit(): void {
    this.store.dispatch(ResetLearnerCourseState());
    this.initQueryParams();
    this.initStoreLoaders();
    this.initStoreSelectors();
    this.dispatchSetCourseId();
    this.initLearnerCourseFetch();
  }

  initQueryParams(): void {
    this.route.queryParams.subscribe((params) => {
      this.courseId = +params[AppConstants.COURSE_ID];
      
      /**Use this LEARNER param for LearnerId instead of LEARNER_ID
       * because visited object will not update in External-Runtime
       * because it will not pass the updateLearnerObject service
       * due to the condition of having a LEARNER_ID param
       */
      this.learnerId = +params[AppConstants.LEARNER];
      this.isNewDigLit = params[AppConstants.IS_NEW_DIG_LIT]
        ? JSON.parse(params[AppConstants.IS_NEW_DIG_LIT])
        : false;
      this.courseIconPath = params[AppConstants.COURSE_ICON_PATH];
    });
  }

  dispatchGetLearnerCourse(): void {
    this.store.dispatch(GetLearnerCourse({learnerId: this.learnerId, courseId: this.courseId}));
  }

  dispatchSetCourseId(): void {
    this.store.dispatch(SetCourseId({ courseId: this.courseId }));
  }

  dispatchGetLearnerCourseLog(): void {
    this.store.dispatch(GetLearnersLearnerCourseLogStatus({courseId: this.courseId}));
  }

  dispatchGetOwnUserProfile(): void {
    this.store.dispatch(GetOwnUserProfile());
  }

  dispatchUpdateLearnerCourse(): void {
    this.store.dispatch(UpdateLearnerCourse({updated: this.updateLearnerCourse}))
  }

  initStoreLoaders(): void {
    this.combinedLoaders$ = combineLatest([
      this.store.select(selectLearnerCourseLoader),
      this.store.select(selectUpdateLearnerCourseLoader),
      this.store.select(selectLearnerCourseLogStatusLoader)
    ]).pipe(
      map(([
        learnerCourse,
        updateLearnerCourse,
        learnerCourseLog,
      ]) => 
        learnerCourse || updateLearnerCourse || learnerCourseLog
      )
    );
  }

  initStoreSelectors(): void {
    /** Selector to fetch the learner course */
    this.store.select(selectLearnerCourseResult)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: LearnerCourse) => {
        /** Ignore the null emissions from state reset / in-flight fetch so the
         * log status is only fetched once the learner course has actually loaded.
         * Otherwise a premature status fetch grabs a stale step. */
        if(!result) return;

        /** Check if skipped value is null */
        if(result.skipped === null) {
          this.updateLearnerCourse = {
            id: result.id,
            courseId: result.courseId,
            assignedBy: result.assignedBy,
            dateAssigned: result.dateAssigned,
            dateUpdated: result.dateUpdated,
            learnerId: result.learnerId,
            startLevel: result.startLevel,
            /**Assigned to WEST value since learner visit the course */
            skipped: AppConstants.WEST_RESULT,
          }
          
          /**Call dispatch to update the learner course skipped value*/
          this.dispatchUpdateLearnerCourse();
        }
        else {
          this.dispatchGetLearnerCourseLog();
        }
      });

    this.store.select(selectLearnerCourseLogStatus)
      .pipe(takeUntil(this.destroy$))
      .subscribe((learnerCourseLog: LearnerCourseLog) => {
        if(learnerCourseLog) {
          /** Status has finished loading; safe to render the appropriate screen. */
          this.statusResolved = true;
          this.componentUrl = learnerCourseLog.componentUrl;
          this.lcLogId = learnerCourseLog.id;
          this.learnerId = learnerCourseLog.learnerId;

          if(this.learnerId) {
            this.dispatchGetOwnUserProfile();
          }

          if (learnerCourseLog.componentUrl == Steps.Learning)
            this.navigateToLearningPage();
        }
      });
    
    this.store.select(selectLearnerCourseLogError)
      .pipe(takeUntil(this.destroy$))
      .subscribe((error: any) => {
        if(error) {
          this.router.navigate([AppConstants.DASHBOARD_PAGE]);
        }
      });

    this.store.select(selectUpdateLearnerCourseResult)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if(result) {
          this.dispatchGetLearnerCourseLog();
        }
      });
    
    this.store.select(selectLearnerCourseLogStatusError)
      .pipe(takeUntil(this.destroy$))
      .subscribe((error: HttpErrorResponse) => {
        if(error) {
          this.router.navigate([AppConstants.DASHBOARD_PAGE]);
        }
      });
  }

  navigateToLearningPage(): void {
    this.router.navigate([AppConstants.LEARNING_PAGE], {
      queryParamsHandling: AppConstants.MERGE,
      relativeTo: this.route,
    });
  }
  
  initLearnerCourseFetch(): void {
    /** If a SCORM assessment completion is still saving, wait for it to finish
     * before fetching so the backend returns the latest learner course log
     * status. Otherwise fetch immediately. */
    const sessionProgressStatus = sessionStorage.getItem(AppConstants.SCORM_COMPLETION_INPROGRESS_KEY);
    if(sessionProgressStatus) {
      this.userSessionService.onDisplayLoader$
        .pipe(
          filter((isLoading: boolean) => isLoading === false),
          take(1),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          sessionStorage.removeItem(AppConstants.SCORM_COMPLETION_INPROGRESS_KEY);
          this.dispatchGetLearnerCourse();
        });
    } else {
      this.dispatchGetLearnerCourse();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }
}
